import { Dialog as ArkDialog, Portal as ArkPortal } from "@ark-ui/react";
import type React from "react";
import { twJoin } from "tailwind-merge";

import { IconButton } from "@/design-system/IconButton";
import { isPortalDropdownTarget } from "@/helpers/portal-dropdown";

export type DrawerBaseProps = {
  isOpened: boolean;
  onOpenChange: (state: boolean) => void;
};

export type DrawerRootProps = DrawerBaseProps & {
  title: string;
  closeLabel: string;
  children: React.ReactNode;
  "data-testid"?: string;
  closeButtonTestId?: string;
};

function DrawerRoot({
  title,
  closeLabel,
  children,
  "data-testid": dataTestId,
  closeButtonTestId,
  isOpened,
  onOpenChange,
}: DrawerRootProps): React.ReactNode {
  return (
    <ArkDialog.Root
      open={isOpened}
      onOpenChange={(details) => onOpenChange(details.open)}
      onInteractOutside={(event) => {
        // Portaled controls still belong to the drawer, as they do in Modal.
        if (isPortalDropdownTarget(event.detail.originalEvent.target)) {
          event.preventDefault();
        }
      }}
      lazyMount
      unmountOnExit
    >
      <ArkPortal>
        <ArkDialog.Backdrop
          className={twJoin(
            "fixed inset-0 z-backdrop cursor-pointer bg-background-default-inverse/60 drawer-motion motion-reduce:animate-none motion-reduce:transition-none",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out",
            "data-[state=open]:animate-in data-[state=open]:fade-in",
          )}
        />
        <ArkDialog.Positioner className="fixed right-0 bottom-0 z-overlay h-fit max-h-dvh w-full md:top-0 md:h-full md:w-fit">
          <ArkDialog.Content
            className={twJoin(
              "ease @container flex drawer-compact-height max-h-dvh w-full flex-col overflow-hidden rounded-t-lg bg-background-default drawer-motion outline-none motion-reduce:animate-none motion-reduce:transition-none md:h-full md:drawer-wide-width md:rounded-l-lg md:rounded-tr-none",
              "data-[state=open]:animate-in max-md:data-[state=open]:slide-in-from-bottom md:data-[state=open]:slide-in-from-right",
              "data-[state=closed]:animate-out max-md:data-[state=closed]:slide-out-to-bottom md:data-[state=closed]:slide-out-to-right",
            )}
            data-testid={dataTestId}
          >
            <div className="flex w-full shrink-0 items-center justify-between gap-space-md border-b border-b-border-secondary p-space-lg">
              <ArkDialog.Title asChild>
                <h3 className="text-heading-3 leading-none">{title}</h3>
              </ArkDialog.Title>
              <ArkDialog.CloseTrigger asChild>
                <IconButton
                  data-testid={closeButtonTestId}
                  variant="soft"
                  title={closeLabel}
                  withTooltip={false}
                  size="sm"
                  isCircular
                  icon={<CloseIcon />}
                />
              </ArkDialog.CloseTrigger>
            </div>
            {children}
          </ArkDialog.Content>
        </ArkDialog.Positioner>
      </ArkPortal>
    </ArkDialog.Root>
  );
}

function CloseIcon(): React.ReactElement {
  return (
    <svg aria-hidden="true" className="size-icon-inline shrink-0" viewBox="0 0 24 24" fill="none">
      <path
        d="M18 6L6 18M6 6L18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface DrawerBodyProps {
  children: React.ReactNode;
}

function DrawerBody({ children }: DrawerBodyProps): React.ReactNode {
  return <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto p-space-lg">{children}</div>;
}

interface DrawerCloseProps {
  children: React.ReactNode;
}

function DrawerClose({ children }: DrawerCloseProps): React.ReactNode {
  return <ArkDialog.CloseTrigger asChild>{children}</ArkDialog.CloseTrigger>;
}

interface DrawerActionsProps {
  children: React.ReactNode;
}

function DrawerActions({ children }: DrawerActionsProps): React.ReactNode {
  return (
    <div className="flex w-full shrink-0 items-center justify-end gap-space-sm border-t border-border-secondary p-space-lg">
      {children}
    </div>
  );
}

export const Drawer = {
  Root: DrawerRoot,
  Body: DrawerBody,
  Actions: DrawerActions,
  Close: DrawerClose,
};
